import { FormControl } from '@angular/forms';

import { CatsRecordTypeEnum, CatsStatusCodeEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy } from '@shared-helpers/array-object-helpers';
import { limitDecimalDisplay, trimDecimalZeros } from '@shared-helpers/string-number-helpers';
import { IForemanCatsProperties, ISkfConfigEntity, ISupportedSkfTypes } from '@shared-interfaces/api';

import { DatahubService } from '../../app/services/datahub.service';
import { ICreateProductionQuantity, IModifyCatsRecordQuantity } from '../models/cats-outbound.model';
import { ISelectOptions } from './productionText';

export class ProductionQuantity {
  value = new FormControl<string>('', { nonNullable: true });
  description: string;
  catsRecord?: IForemanCatsProperties;
  skfNumber: string;
  weDateTotal = 0;
  prdGrpID = '';
  type: ISupportedSkfTypes = 'PRODUCTION';
  selectOptions: ISelectOptions[] = [];

  private isNewRecord = true;
  private isModifiedRecord = false;
  private hasChanged: boolean;
  private previousValue = 0;
  private readonly listener;

  constructor(
    skfConfig: ISkfConfigEntity,
    private readonly datahub: DatahubService,
    groupNumber?: string,
  ) {
    this.description = skfConfig.CustDesc;
    this.skfNumber = skfConfig.SKF;
    if (groupNumber) this.prdGrpID = groupNumber;
    this.hasChanged = false;
    this.listener = this.value.valueChanges.subscribe((value) => {
      //when the value changes in the form, update the changed flag.
      this.updateWeeklyTotal(Number(value));
      this.hasChanged = true;
    });
  }

  updateWeeklyTotal(value: number): void {
    //calc the difference in the values to update the weekly total.
    const difference = value - this.previousValue;
    this.weDateTotal += difference;
    this.previousValue = value;
  }

  setState(catsRecord: IForemanCatsProperties): void {
    //the contract handler will initialize the method. The timesheet handler will set the state, if any.
    if (Number(catsRecord.Status) > CatsStatusCodeEnum.ReadyForApproval) {
      this.setControlValue(catsRecord.Quantity, true, 'alreadyApprovedError');
    } else this.value.setValue(trimDecimalZeros(catsRecord.Quantity), { emitEvent: false });
    this.previousValue = Number(this.value.value);
    this.catsRecord = catsRecord;
    this.hasChanged = false;
    this.isModifiedRecord = true;
    this.isNewRecord = false;
  }

  private setControlValue(value: string, isDisabled: boolean, error?: string): void {
    this.value = new FormControl<string>({ value: value, disabled: isDisabled }, { nonNullable: true });
    this.value.setValue(trimDecimalZeros(value));
    if (error) {
      this.value.setErrors({ error: true });
    }
  }

  findWeeklyTotal(weeklyRecords: IForemanCatsProperties[]): void {
    let sum = 0;
    if (arrayIsTruthy(weeklyRecords)) {
      weeklyRecords.forEach((record) => {
        if (record.Statkeyfig === this.skfNumber) sum += Number(record.Quantity);
      });
    }
    this.weDateTotal = sum;
  }

  getCatRecords(): ICreateProductionQuantity | IModifyCatsRecordQuantity | null {
    //this method will be called when the application data is serialized for upload. It should only return a record when the changed flag is true. If we are working off a modified cat record, then we can just return that.
    if (!this.hasChanged) return null;
    if (this.isNewRecord) return this.makeProductionSkfCatRecord(this.datahub);
    if (this.isModifiedRecord) return this.formatModify(this.catsRecord);
    return null;
  }

  makeProductionSkfCatRecord(datahub: DatahubService): ICreateProductionQuantity {
    return {
      ZempCreatedBy: datahub.timesheet.findTokenEmployee(),
      Extdocumentno: datahub.extDocument.Documentno,
      Zmatnr: datahub.contract.getProductionDefault(this.skfNumber),
      Employeenumber: datahub.timesheet.findTokenEmployee(),
      Quantity: this.value.value.trim() === '' ? '0' : this.value.value,
      Workdate: datahub.date.getOdataString(),
      RecOrder: datahub.contract.getServiceOrder(),
      Statkeyfig: this.skfNumber,
      Zrecordtype: CatsRecordTypeEnum.ProductionQuantity,
      ZprdGrpID: this.prdGrpID,
    };
  }

  private formatModify(record?: IForemanCatsProperties): IModifyCatsRecordQuantity {
    //the only thing we should be able to change in the foreman screen is the quantity.
    return {
      Counter: record?.Counter ?? '',
      Quantity: this.value.value.trim() === '' ? '0' : this.value.value,
      Quantity_X: true,
    };
  }

  removeSelf(productionArray: ProductionQuantity[]): void {
    const index = productionArray.findIndex((production) => production.skfNumber === this.skfNumber);
    if (index < 0) return;
    productionArray.splice(index, 1);
    this.listener.unsubscribe();
  }

  getGraphableValues() {
    return [
      this.description,
      '',
      '',
      '',
      trimDecimalZeros(limitDecimalDisplay(this.value.value)),
      trimDecimalZeros(limitDecimalDisplay(this.weDateTotal)),
    ];
  }

  findCounters(): string {
    if (this.catsRecord?.Counter) return this.catsRecord.Counter;
    else return '';
  }
}
